import {
  Component, Input, OnChanges, AfterViewInit,
  ViewChild, ElementRef, NgZone,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RecognitionVisualizationConfig } from '../bbox-gallery-card/bbox-gallery-card.component';

// ─────────────────────────────────────────────────────────────────────────────

const TAG_META: Record<string, { label: string; color: string }> = {
  postobon:    { label: 'Postobon',    color: '#6366f1' },
  competencia: { label: 'Competencia', color: '#f43f5e' },
  precios:     { label: 'Precios',     color: '#f59e0b' },
  nevera:      { label: 'Nevera',      color: '#14b8a6' },
};

const GRID = 60;

@Component({
  selector: 'app-detection-heatmap-card',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './detection-heatmap-card.component.html',
  styleUrl:    './detection-heatmap-card.component.scss',
})
export class DetectionHeatmapCardComponent implements OnChanges, AfterViewInit {
  @Input() config!: RecognitionVisualizationConfig;
  @Input() loading = false;

  @ViewChild('heatCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  activeTag      = 'competencia';
  tags: { key: string; label: string; color: string }[] = [];

  // ── Controles ─────────────────────────────────────────────────────────────
  scoreThreshold = 50;    // 0-100, filtra objetos con score < umbral
  includeOthers  = false; // excluir "Others" por defecto (son ruido)

  // ── Stats del tag activo + filtros actuales ───────────────────────────────
  totalDetections  = 0;
  filteredOut      = 0;
  topObject        = '';
  avgScore         = 0;
  othersCount      = 0;

  private refW = 1;
  private refH = 1;
  private viewReady = false;

  constructor(private zone: NgZone) {}

  ngOnChanges(): void {
    this.build();
    if (this.viewReady) this.renderHeatmap();
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.renderHeatmap();
  }

  // ── Build ─────────────────────────────────────────────────────────────────

  private build(): void {
    if (!this.config?.rows?.length) return;

    const tagKeys = [...new Set(this.config.rows.flatMap(r => r.objects.map(o => o.tag)))];
    this.tags = tagKeys
      .filter(t => TAG_META[t])
      .map(t => ({ key: t, label: TAG_META[t].label, color: TAG_META[t].color }));

    if (!this.tags.find(t => t.key === this.activeTag) && this.tags.length) {
      this.activeTag = this.tags[0].key;
    }

    let maxX = 1, maxY = 1;
    for (const row of this.config.rows) {
      for (const obj of row.objects) {
        if (obj.bbox.x_max > maxX) maxX = obj.bbox.x_max;
        if (obj.bbox.y_max > maxY) maxY = obj.bbox.y_max;
      }
    }
    this.refW = maxX;
    this.refH = maxY;
    this.computeStats();
  }

  private computeStats(): void {
    const allTag = this.config?.rows
      ?.flatMap(r => r.objects)
      .filter(o => o.tag === this.activeTag) ?? [];

    this.othersCount = allTag.filter(o => o.name === 'Others').length;

    const filtered = allTag.filter(o =>
      (o.score * 100) >= this.scoreThreshold &&
      (this.includeOthers || o.name !== 'Others')
    );

    this.totalDetections = filtered.length;
    this.filteredOut     = allTag.length - filtered.length;

    if (!filtered.length) { this.topObject = '—'; this.avgScore = 0; return; }

    const nameCount: Record<string, number> = {};
    let totalScore = 0;
    for (const o of filtered) {
      nameCount[o.name] = (nameCount[o.name] ?? 0) + 1;
      totalScore += o.score;
    }
    this.topObject = Object.entries(nameCount).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '—';
    this.avgScore  = Math.round((totalScore / filtered.length) * 100);
  }

  // ── Controles ─────────────────────────────────────────────────────────────

  setTag(key: string): void {
    this.activeTag = key;
    this.computeStats();
    this.renderHeatmap();
  }

  onThresholdChange(): void {
    this.computeStats();
    this.renderHeatmap();
  }

  onOthersToggle(): void {
    this.computeStats();
    this.renderHeatmap();
  }

  // ── Render ────────────────────────────────────────────────────────────────

  renderHeatmap(): void {
    if (!this.canvasRef || !this.config?.rows?.length) return;
    this.zone.runOutsideAngular(() => this.draw());
  }

  private draw(): void {
    const canvas = this.canvasRef.nativeElement;
    const ctx    = canvas.getContext('2d');
    if (!ctx) return;

    const density = Array.from({ length: GRID }, () => new Float32Array(GRID));

    for (const row of this.config.rows) {
      for (const obj of row.objects) {
        if (obj.tag !== this.activeTag) continue;
        if ((obj.score * 100) < this.scoreThreshold) continue;
        if (!this.includeOthers && obj.name === 'Others') continue;

        const c0 = Math.floor((obj.bbox.x_min / this.refW) * GRID);
        const r0 = Math.floor((obj.bbox.y_min / this.refH) * GRID);
        const c1 = Math.ceil( (obj.bbox.x_max / this.refW) * GRID);
        const r1 = Math.ceil( (obj.bbox.y_max / this.refH) * GRID);

        for (let r = Math.max(0, r0); r < Math.min(GRID, r1); r++) {
          for (let c = Math.max(0, c0); c < Math.min(GRID, c1); c++) {
            density[r][c] += obj.score;
          }
        }
      }
    }

    let maxVal = 0;
    for (let r = 0; r < GRID; r++)
      for (let c = 0; c < GRID; c++)
        if (density[r][c] > maxVal) maxVal = density[r][c];

    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = '#0a0a14';
    ctx.fillRect(0, 0, W, H);

    if (maxVal === 0) return;

    const cellW = W / GRID;
    const cellH = H / GRID;

    for (let r = 0; r < GRID; r++) {
      for (let c = 0; c < GRID; c++) {
        const t = density[r][c] / maxVal;
        if (t < 0.01) continue;
        ctx.fillStyle = this.heatColor(t, this.activeTag);
        ctx.fillRect(
          Math.round(c * cellW), Math.round(r * cellH),
          Math.ceil(cellW) + 1,  Math.ceil(cellH) + 1,
        );
      }
    }

    // Grid guide sutil
    ctx.strokeStyle = 'rgba(255,255,255,.03)';
    ctx.lineWidth   = 0.5;
    for (let i = 0; i <= GRID; i += 10) {
      ctx.beginPath(); ctx.moveTo(i * cellW, 0);  ctx.lineTo(i * cellW, H); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, i * cellH);  ctx.lineTo(W, i * cellH); ctx.stroke();
    }

    // Leyenda
    this.drawLegend(ctx, W, H);
  }

  private drawLegend(ctx: CanvasRenderingContext2D, W: number, H: number): void {
    const lW = 10, lH = 100, lX = W - lW - 10, lY = H / 2 - lH / 2;
    const grad = ctx.createLinearGradient(0, lY, 0, lY + lH);
    grad.addColorStop(0,   this.heatColor(1.0, this.activeTag));
    grad.addColorStop(0.5, this.heatColor(0.5, this.activeTag));
    grad.addColorStop(1,   this.heatColor(0.1, this.activeTag));
    ctx.fillStyle = grad;
    ctx.beginPath(); ctx.roundRect(lX, lY, lW, lH, 3); ctx.fill();

    ctx.font      = '10px system-ui, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,.55)';
    ctx.textAlign = 'right';
    ctx.fillText('Alto', lX - 4, lY + 8);
    ctx.fillText('Bajo', lX - 4, lY + lH);
  }

  private heatColor(t: number, tag: string): string {
    const hues: Record<string, [number, number]> = {
      postobon:    [240, 270],
      competencia: [0,   30 ],
      precios:     [40,  60 ],
      nevera:      [170, 190],
    };
    const [hLow, hHigh] = hues[tag] ?? [0, 30];
    const h = hLow + (hHigh - hLow) * t;
    const l = 30 + 35 * t;
    const a = 0.15 + 0.75 * t;
    return `hsla(${h},85%,${l}%,${a})`;
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  activeTagMeta(): { label: string; color: string } {
    return TAG_META[this.activeTag] ?? { label: this.activeTag, color: '#94a3b8' };
  }

  get thresholdLabel(): string { return `${this.scoreThreshold}%`; }
}
